/* Enhances existing controls; their original navigation handlers remain responsible for routing. */
(() => {
  if (window.caNubeCloudEffects) return;
  const selector = '.ca-nube-vertical-hint, .ca-nube-horizontal-hint';
  const originalPath = 'M8 20 C15 20 22 20 31 20 M22 11 C24.5 14.5 28 18 31 20 C28 22 24.5 25.5 22 29';
  // A tapered, slightly curved stroke rather than a uniform stock arrow.
  const drawnPath = 'M12 34 C20 33 30 29 44 30 C40 27 36 23 33 19 C32 17 33 15 35 15 C36 15 37 16 38 17 C43 22 49 26 54 28 C57 29 57 32 54 34 C48 37 43 42 39 47 C37 49 34 48 34 46 C34 45 35 44 36 42 C38 39 41 36 44 34 C32 33 23 37 13 39 C9 40 9 35 12 34 Z';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const controls = new Map();
  const forwarding = new WeakSet();
  let enhanced = true;
  let frame = 0;
  let previousTime = 0;

  const direction = button => button.classList.contains('is-right') ? [1, 0] : button.classList.contains('is-left') ? [-1, 0] : [0, 1];
  const rest = () => ({ x: 0, y: 0, sx: 1, sy: 1, reach: 0, tilt: 0 });
  const targetFor = state => {
    if (!enhanced || reduced.matches) return rest();
    const [dx, dy] = direction(state.button);
    const engaged = state.over || state.focused;
    const lift = state.held ? -3 : engaged ? 19 : 0;
    const lateral = engaged && !state.held ? state.pointer * 7 : 0;
    const across = state.held ? 1.09 : engaged ? .98 : 1;
    const along = state.held ? .82 : engaged ? 1.09 : 1;
    return {
      x: -dx * lift + dy * lateral,
      y: -dy * lift + dx * lateral,
      sx: dx ? along : across,
      sy: dy ? along : across,
      reach: state.firing ? 23 : state.held ? -5 : engaged ? 7 : 0,
      tilt: state.held || state.firing ? 0 : state.pointer * 4
    };
  };
  const draw = state => {
    const p = state.position;
    state.body.style.transform = `translate3d(${p.x}px,${p.y}px,0) scale(${p.sx},${p.sy})`;
    state.arrow.style.setProperty('--ca-arrow-reach', `${p.reach}px`);
    state.arrow.style.setProperty('--ca-arrow-tilt', `${p.tilt}deg`);
    state.arrow.style.opacity = state.firing ? String(Math.max(0, 1 - Math.max(0, p.reach - 8) / 15)) : '';
  };
  const tick = time => {
    const dt = Math.min((time - previousTime) / 1000 || 1 / 60, 1 / 30);
    previousTime = time;
    let moving = false;
    for (const state of controls.values()) {
      if (!state.button.isConnected || !enhanced || reduced.matches) continue;
      const target = targetFor(state);
      for (const key of Object.keys(target)) {
        const delta = target[key] - state.position[key];
        state.velocity[key] += (delta * 340 - state.velocity[key] * 27) * dt;
        state.position[key] += state.velocity[key] * dt;
        const tolerance = key === 'sx' || key === 'sy' ? .0005 : .02;
        if (Math.abs(delta) > tolerance || Math.abs(state.velocity[key]) > tolerance) moving = true;
        else { state.position[key] = target[key]; state.velocity[key] = 0; }
      }
      draw(state);
    }
    frame = moving ? requestAnimationFrame(tick) : 0;
  };
  const wake = () => { if (!frame && enhanced && !reduced.matches) { previousTime = performance.now(); frame = requestAnimationFrame(tick); } };
  const reset = state => {
    clearTimeout(state.timer);
    state.over = false; state.held = false; state.firing = false; state.busy = false; state.pointer = 0;
    state.position = rest(); state.velocity = Object.fromEntries(Object.keys(rest()).map(key => [key, 0]));
    state.body.style.transform = '';
    state.arrow.style.cssText = '';
    state.button.removeAttribute('aria-busy');
    state.button.removeAttribute('data-ca-label-dismissed');
  };
  const syncLabel = state => {
    const text = state.button.dataset.caCloudDestination || '';
    if (state.label.textContent !== text) state.label.textContent = text;
    state.label.hidden = !text;
  };
  const applyMode = state => {
    reset(state);
    state.button.classList.toggle('ca-cloud-enhanced', enhanced);
    state.arrow.setAttribute('viewBox', enhanced ? '0 0 64 64' : '0 0 40 40');
    state.arrow.querySelector('path').setAttribute('d', enhanced ? drawnPath : originalPath);
    wake();
  };
  const prepare = button => {
    if (controls.has(button)) return;
    const cloud = button.querySelector('.ca-nube-vertical-hint__cloud, .ca-nube-horizontal-hint__cloud');
    const arrow = cloud?.querySelector('svg');
    if (!arrow) return;
    const body = document.createElement('span');
    body.className = 'ca-cloud-body';
    cloud.before(body); body.append(cloud);
    const label = document.createElement('span');
    label.className = 'ca-cloud-destination';
    label.setAttribute('aria-hidden', 'true');
    button.append(label);
    const state = { button, body, arrow, label, focused: false };
    controls.set(button, state);
    syncLabel(state);
    applyMode(state);
    if (enhanced && !reduced.matches) {
      const [dx, dy] = direction(button);
      state.position.x = dx * 25; state.position.y = dy * 25;
      wake();
    }
    button.addEventListener('pointerenter', event => {
      if (!enhanced || !finePointer.matches || event.pointerType === 'touch') return;
      button.removeAttribute('data-ca-label-dismissed');
      state.over = true; wake();
    });
    button.addEventListener('pointermove', event => {
      if (!enhanced || !state.over || state.busy) return;
      const rect = button.getBoundingClientRect();
      const horizontal = direction(button)[0] !== 0;
      state.pointer = Math.max(-1, Math.min(1, horizontal ? (event.clientY - rect.top) / rect.height * 2 - 1 : (event.clientX - rect.left) / rect.width * 2 - 1));
      wake();
    });
    button.addEventListener('pointerleave', () => { state.over = false; state.held = false; state.pointer = 0; wake(); });
    button.addEventListener('pointerdown', event => { if (event.button === 0 && !state.busy) { state.held = true; wake(); } });
    button.addEventListener('pointerup', () => { state.held = false; wake(); });
    button.addEventListener('pointercancel', () => { state.held = false; state.over = false; wake(); });
    button.addEventListener('focus', () => { button.removeAttribute('data-ca-label-dismissed'); state.focused = button.matches(':focus-visible'); wake(); });
    button.addEventListener('blur', () => { state.focused = false; state.held = false; wake(); });
    button.addEventListener('keydown', event => {
      if (event.key === 'Escape') button.setAttribute('data-ca-label-dismissed', '');
      if (['Enter', ' '].includes(event.key) && !state.busy) { state.held = true; wake(); }
    });
    button.addEventListener('keyup', () => { state.held = false; wake(); });
    button.addEventListener('click', event => {
      if (!enhanced || reduced.matches || forwarding.has(button)) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (state.busy) return;
      state.busy = true; state.held = false; state.firing = true;
      const [dx, dy] = direction(button);
      state.position.sx = dx ? .82 : 1.09;
      state.position.sy = dy ? .82 : 1.09;
      state.position.reach = -5;
      draw(state);
      button.setAttribute('aria-busy', 'true');
      wake();
      state.timer = setTimeout(() => {
        if (button.isConnected && !button.disabled) {
          forwarding.add(button);
          button.click();
          forwarding.delete(button);
        }
        reset(state);
      }, 160);
    }, true);
  };
  document.querySelectorAll(selector).forEach(prepare);
  new MutationObserver(records => {
    for (const record of records) {
      if (record.type === 'attributes') {
        const state = controls.get(record.target);
        if (state) syncLabel(state);
        continue;
      }
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches(selector)) prepare(node);
        node.querySelectorAll(selector).forEach(prepare);
      }
    }
    for (const [button, state] of controls) {
      if (!button.isConnected) { reset(state); controls.delete(button); }
    }
  }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-ca-cloud-destination'] });
  reduced.addEventListener('change', () => controls.forEach(applyMode));
  window.caNubeCloudEffects = {
    setMode(mode) { enhanced = mode !== 'original'; controls.forEach(applyMode); },
    replay() {
      for (const state of controls.values()) {
        if (!state.button.classList.contains('is-active') || reduced.matches) continue;
        if (enhanced) {
          const [dx, dy] = direction(state.button);
          state.position.x = dx * 25; state.position.y = dy * 25;
          state.position.sx = dx ? .8 : 1.08; state.position.sy = dy ? .8 : 1.08;
          wake();
        } else {
          state.button.classList.remove('is-breathing');
          requestAnimationFrame(() => { state.button.classList.add('is-breathing'); setTimeout(() => state.button.classList.remove('is-breathing'), 1500); });
        }
      }
    }
  };
})();
